<template>
  <div class="w-full">
    <NuxtLink to="#how-to">
      <h2 id="how-to"><DocsIconBookmark /> How to?</h2>
    </NuxtLink>

    <DocsHowToConfigure :project />

    <NuxtLink to="#how-to-handle-envs">
      <h3 id="how-to-handle-envs">How to handle envs?</h3>
    </NuxtLink>
    <div>
      <DocsInfoBlock class="mb-6">
        <div>
          It's important to pass the right environment variables when:
          <ul class="mt-1 list-disc list-inside">
            <li>Building your app</li>
            <li>Calling Otago doctor</li>
            <li>Deploying an update</li>
          </ul>
        </div>
      </DocsInfoBlock>

      <div class="mb-6">
        <h4 class="mb-2"><code>1.</code> Envs automatic loading:</h4>
        <div class="mt-3">
          By default, Otago CLI will load:
          <ul class="list-disc list-inside">
            <li>
              All your <code>.env</code> files with <code>NODE_ENV=production</code>. See
              <ExternalLink
                href="https://github.com/bkeepers/dotenv/blob/c6e583a/README.md#what-other-env-files-can-i-use"
              >
                override order
              </ExternalLink>
            </li>
            <li v-if="config_options.expo === 'yes'">
              All env variables in your <code>eas.json</code> with <code>EAS_PROFILE=production</code> (lower priority)
            </li>
          </ul>
          <div class="mt-3">
            You can override <code>NODE_ENV</code>
            <span v-if="config_options.expo === 'yes'"> and <code>EAS_PROFILE</code></span> with the values of your
            choice.
          </div>
          <div class="mt-1">
            You can also disable this automatic loading with <code>OTAGO_LOAD_ENVS=false</code> and inline or export all
            the variables you need.
          </div>
        </div>
      </div>

      <div class="mb-4">
        <h4 class="mb-2"><code>2.</code> Multiple deployment envs:</h4>
        <div class="mb-4">
          If you have multiple versions of your app (preview, staging, production... except development because it makes
          little sens to update it with Otago) and you want to be able to push different code updates to each of them,
          all you need to do is build them with a different Otago update URL.
        </div>
        <DocsDiffBlock class="mb-2" title="# app.config.js (dynamic config)" :content="multi_update_url_config" />
        <div>
          Then remember to pass the right <code>OTAGO_UPDATE_URL=...</code> when you build your app.
          <DocsDiffBlock v-if="config_options.expo === 'yes'" class="mt-2" title="# eas.json" :content="eas_config" />
        </div>
      </div>
    </div>

    <!-- https://docs.expo.dev/eas-update/code-signing/ -->
    <NuxtLink to="#how-to-sign-deployments">
      <h3 id="how-to-sign-deployments">How to sign deployments?</h3>
    </NuxtLink>
    <div>
      <DocsInfoBlock class="mb-6">
        The signatures are verified on the client before the updates are applied.
        <div class="h-2" />
        We highly recommend signing code updates, to ensure no one can tamper with your updates.
      </DocsInfoBlock>

      <div class="mb-6">
        <h4 class="mb-2"><code>1.</code> Generate a private key:</h4>
        <DocsDiffBlock class="mb-2" :content="generate_pk_command" />
        <div>
          Important: do NOT commit the content of the <code>keys</code> folder, it contains your private key, which
          should be kept secret.
        </div>
      </div>

      <div class="mb-6">
        <h4 class="mb-2"><code>2.</code> Configure your app's builds to use code signing:</h4>
        <DocsDiffBlock :content="configure_pk_command" />
      </div>

      <div class="mb-6">
        <h4 class="mb-2"><code>3.</code> Re-build and publish your app.</h4>
        <div>Important: you need to increment your runtime version when you add (or remove) code signing.</div>
      </div>

      <div class="mb-6">
        <h4 class="mb-2"><code>4.</code> Pass the private key when you deploy an update:</h4>
        <DocsDiffBlock :content="`$OTAGO_PRIVATE_KEY=keys/private-key.pem npx otago deploy (...)`" />
      </div>
    </div>

    <NuxtLink to="#how-to-deploy">
      <h3 id="how-to-deploy">How to deploy?</h3>
    </NuxtLink>
    <div>
      <DocsInfoBlock class="mb-6">
        <ul class="mb-3">
          <li><span class="text-green-400">✓ Can be updated:</span> Javascript code, images, fonts...</li>
          <li>
            <span class="text-red-500">✗ Cannot be updated:</span> Native code, app config, libraries that import native
            code...
          </li>
        </ul>
        <div>⚠ Important: your updates always need to be compatible with your app runtime version!</div>
      </DocsInfoBlock>

      <div class="mb-6">
        <h4 class="mb-2"><code>1.</code> Check runtime version:</h4>
        <div>
          If you install or upgrade a lib which includes some native code or configuration, you cannot push it as a code
          update with Otago. Instead you need to increment runtime version (this is automatic with
          <code>fingerprint</code> policy), then build and publish a new version of your app.
        </div>
      </div>

      <div class="mb-6">
        <h4 class="mb-2"><code>2.</code> Run deployment:</h4>
        <DocsDiffBlock
          class="mb-2"
          :content="`$OTAGO_API_KEY=${generatedKey || '[api_key]'} npx otago deploy --project ${projectName}`"
        />
      </div>
    </div>

    <!-- <NuxtLink to="#how-to-deploy-from-ci">
      <h3 id="how-to-deploy-from-ci">How to deploy from CI?</h3>
    </NuxtLink>
    <div>
      <div>TODO v2: How to deploy from CI?</div>
      <div>TODO v2: /!\ runtime version check?</div>
      <div>TODO v2: CLI params as env vars notice</div>
      <div>TODO v2: Github action</div>
    </div> -->
  </div>
</template>

<script setup lang="ts">
import type { CompanyProjectDto } from "~/server/serializers";

const props = defineProps<{
  project?: CompanyProjectDto;
}>();

const config_options = useConfigOptions();
const projectName = computed(() => props.project?.slug || "[project_ref]");

const generatedKey = ref(import.meta.browser ? JSON.parse(sessionStorage.getItem("api_keys") || "[]")[0]?.key : null);

const multi_update_url_config = computed(
  () => ` {
   "expo": {
     "updates": {
-      "url": "...",
+      "url": process.env.OTAGO_UPDATE_URL,
     },
     ...
`,
);

const generate_pk_command = `$npx expo-updates codesigning:generate \\
    --key-output-directory keys \\
    --certificate-output-directory certs \\
    --certificate-validity-duration-years 20 \\
    --certificate-common-name "My App"
`;
const configure_pk_command = `$npx expo-updates codesigning:configure \\
    --certificate-input-directory certs \\
    --key-input-directory keys
`;

const eas_config = ` {
   "build": {
     "preview": {
       ...
       "env": {
+        "OTAGO_UPDATE_URL": "..."
       }
     },
     "production": {
       ...
       "env": {
+        "OTAGO_UPDATE_URL": "..."
       }
     },
     ...
`;
</script>
